import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { vabsPaymentMethodsQuery } from '@/entities/payment/api';

import { VabsParticipantType, VabsPaymentSystem } from '@/shared/api';
import {
  DashboardDialog,
  Header,
  ScrollArea,
  Skeleton,
  SmallCloseButton,
} from '@/shared/ui';

import { SelectItem } from './SelectItem';
import { SelectTrigger } from './SelectTrigger';

interface PaymentSystemSelectProps {
  onSelect: (item: { id: VabsPaymentSystem; label: string }) => void;
  selected: { id: VabsPaymentSystem; label: string };
  items?: { id: VabsPaymentSystem; label: string }[];
  currency?: string;
  participant?: VabsParticipantType;
}

const Content = ({
  onSelect,
  selected,
  items = [],
}: PaymentSystemSelectProps) => {
  return (
    <>
      <ScrollArea className="flex max-h-[528px] w-full flex-col pl-4 pr-[18px] mb-4">
        {items.map((item, idx) => (
          <SelectItem
            item={item}
            onClick={onSelect}
            isSelected={selected?.id === item.id}
            key={item.id}
          />
        ))}
      </ScrollArea>
    </>
  );
};

export const PaymentSystemSelect = ({
  onSelect,
  selected,
  currency,
  participant,
}: PaymentSystemSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: methods, isPending } = useQuery({
    queryKey: ['payment-methods', currency, participant],
    queryFn: async () =>
      await vabsPaymentMethodsQuery({
        currencyId: currency,
        participantType: participant,
      }),
  });

  return (
    <DashboardDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      contentClassName="gap-0 max-h-[600px]"
      header={
        <Header
          className="p-5"
          title="Payment method"
          action={{
            as: 'custom',
            render: <SmallCloseButton />,
          }}
        />
      }
      content={
        isPending ? (
          <div className="flex max-h-[528px] w-full flex-col px-5">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="my-2">
                <Skeleton className="h-8 w-full rounded-md" />
              </div>
            ))}
          </div>
        ) : (
          <Content
            items={methods?.nodes
              ?.filter((i) => i?.isActive)
              ?.map((i) => ({ id: i.type, label: i.type as string }))}
            onSelect={(item) => {
              onSelect(item);
              setIsOpen(false);
            }}
            selected={selected}
          />
        )
      }
      hideCloseButton
      animationProps={{ isFirst: true }}
    >
      <SelectTrigger
        isOpen={isOpen}
        item={selected}
        placeholder="Payment method"
      />
    </DashboardDialog>
  );
};
